import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { memo, useEffect, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import { SpeechCapture } from './capture/SpeechCapture';
import { MicButton } from './composer/MicButton';
import { VoiceHoldLayers } from './composer/VoiceHoldLayers';
import { HoldZone } from './functions/holdZone';
import { useDictationTurn } from './functions/useDictationTurn';
import { useHoldToTalk } from './functions/useHoldToTalk';
import { useSpokenIntoBox } from './functions/useSpokenIntoBox';

const kNothing = () => undefined;

/**
 * The voice composer's mic on its own, for a host with its own text box (the
 * AI home's prompt card): a tap dictates into the host's text, a ring
 * breathing with the voice; a hold talks under the overlay, and letting go
 * sends the words, cancels, or opens them to edit.
 */
export const DictationMic = memo(
  ({
    value,
    onChangeText,
    onSend,
    canTalk = true,
    onDictatingChange,
  }: {
    value: string;
    onChangeText: (text: string) => void;
    /** Words held and let go of on 发送, or sent from the editor. */
    onSend: (text: string, from: 'hold' | 'editor') => void;
    canTalk?: boolean;
    /** The host's send waits while words are dictated into its text. */
    onDictatingChange?: (dictating: boolean) => void;
  }) => {
    const send = useRef(onSend);
    send.current = onSend;
    const { voice, capture, endpoint } = useDictationTurn(text =>
      send.current(text, 'hold'),
    );
    const zone$ = useCreation(() => new BehaviorSubject<HoldZone>('send'), []);
    const [state] = useBehaviorSubject(voice.state$);
    const dictating = state.kind === 'holding' && state.via === 'dictate';
    const inserting =
      dictating || (state.kind === 'recognizing' && state.outcome === 'insert');
    const dictatingChanged = useRef(onDictatingChange);
    dictatingChanged.current = onDictatingChange;
    useEffect(() => {
      dictatingChanged.current?.(inserting);
    }, [inserting]);
    const spoken = useSpokenIntoBox({
      voice,
      value,
      onChangeText,
      canTalk,
      onOpen: kNothing,
    });
    const mic = useHoldToTalk({
      voice,
      zone$,
      canTalk: canTalk && !inserting,
      onTap: spoken.toggleDictation,
    });
    return (
      <>
        <MicButton
          dictating={dictating}
          canTalk={canTalk}
          level$={voice.level$}
          panHandlers={mic.panHandlers}
        />
        <VoiceHoldLayers
          voice={voice}
          zone$={zone$}
          editing={spoken.editing}
          canSend
          onSendEdited={text => send.current(text, 'editor')}
          onCloseEditor={spoken.closeEditor}
        />
        {endpoint ? (
          <SpeechCapture
            ref={capture}
            endpoint={endpoint}
            onMessage={voice.receive}
          />
        ) : null}
      </>
    );
  },
);
